import React, { useState, useMemo } from "react";
import { View, Text, Pressable, StyleSheet } from "react-native";
import { MotiView } from "moti";
import { UserPlus, Download } from "@phosphor-icons/react";
import { colors, radii, spacing, typographyScale } from "../../../../styles/design-system";
import CustomDropdown, { CustomDropdownOption } from "../../../common/general/CustomDropDown/CustomDropdown";
import type { TriageStatus, StaffMember } from "../SafetySupportScreen";

interface BulkActionsBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkAssign: (assignee: string) => void;
  onBulkStatusChange: (status: TriageStatus) => void;
  staffList: StaffMember[];
}

function ActionButton({ onPress, icon, label }: { onPress?: () => void; icon: React.ReactNode; label: string }) {
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [styles.actionButton, pressed && styles.actionButtonPressed]}>
      {icon}
      <Text style={styles.actionButtonText}>{label}</Text>
    </Pressable>
  );
}

export function BulkActionsBar({
  selectedCount,
  onClearSelection,
  onBulkAssign,
  onBulkStatusChange,
  staffList,
}: BulkActionsBarProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);

  // Staff options for assign dropdown
  const assignOptions: CustomDropdownOption[] = useMemo(
    () =>
      staffList.map((staff) => ({
        label: staff.name,
        value: staff.id,
      })),
    [staffList]
  );

  // Status options for change status dropdown
  const statusOptions: CustomDropdownOption[] = useMemo(
    () => [
      { label: "Mark In Progress", value: "in-progress" },
      { label: "Mark Resolved", value: "resolved" },
    ],
    []
  );

  const handleAssignChange = (value: string) => {
    const staff = staffList.find((s) => s.id === value);
    if (staff) {
      onBulkAssign(staff.name);
    }
  };

  const handleStatusChange = (value: string) => {
    onBulkStatusChange(value as TriageStatus);
  };

  return (
    <MotiView
      from={{ opacity: 0, translateY: -10 }}
      animate={{ opacity: 1, translateY: 0 }}
      exit={{ opacity: 0, translateY: -10 }}
      transition={{ type: "timing", duration: 200 }}
      style={styles.container}>
      {/* Selection Info */}
      <View style={styles.selectionInfo}>
        <Text style={styles.selectedText}>
          {selectedCount} item{selectedCount > 1 ? "s" : ""} selected
        </Text>
        <Pressable onPress={onClearSelection}>
          <Text style={styles.clearText}>Clear selection</Text>
        </Pressable>
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        {/* Assign To */}
        <View style={[styles.dropdownWrapper, assignOpen && styles.dropdownWrapperOpen]}>
          <View style={styles.dropdownWithIcon}>
            <UserPlus size={16} color={colors.white} style={styles.dropdownIcon} />
            <CustomDropdown
              options={assignOptions}
              value={undefined}
              onChange={handleAssignChange}
              placeholder="Assign To"
              placeholderTextColor={colors.white}
              fullWidth={false}
              accessibilityLabel="Assign selected items to staff member"
              open={assignOpen}
              onOpenChange={setAssignOpen}
              dropdownStyle={styles.bulkDropdownStyle}
              dropdownContainerStyle={styles.bulkDropdownContainerStyle}
              showArrow={false}
            />
          </View>
        </View>

        {/* Change Status */}
        <View style={[styles.dropdownWrapper, statusOpen && styles.dropdownWrapperOpen]}>
          <CustomDropdown
            options={statusOptions}
            value={undefined}
            onChange={handleStatusChange}
            placeholder="Change Status"
            placeholderTextColor={colors.white}
            fullWidth={false}
            accessibilityLabel="Change status of selected items"
            open={statusOpen}
            onOpenChange={setStatusOpen}
            dropdownStyle={styles.bulkDropdownStyleNoIcon}
            dropdownContainerStyle={styles.bulkDropdownContainerStyle}
            showArrow={false}
          />
        </View>

        {/* Export */}
        <ActionButton
          icon={<Download size={16} color={colors.white} />}
          label="Export"
          onPress={() => {
            // Export functionality
            console.log("Export selected items");
          }}
        />
      </View>
    </MotiView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: colors.brand[600],
    borderRadius: radii.lg,
    padding: spacing.sm,
    marginBottom: spacing.sm,
    zIndex: 10,
  },
  selectionInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
  },
  selectedText: {
    fontSize: typographyScale.textBase.fontSize,
    lineHeight: typographyScale.textBase.lineHeight,
    fontWeight: "500",
    color: colors.white,
  },
  clearText: {
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    color: "rgba(255, 255, 255, 0.8)",
    textDecorationLine: "underline",
  },
  actions: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.xs,
  },
  dropdownWrapper: {
    zIndex: 1,
  },
  dropdownWrapperOpen: {
    zIndex: 5000,
  },
  dropdownWithIcon: {
    flexDirection: "row",
    alignItems: "center",
  },
  dropdownIcon: {
    position: "absolute",
    left: spacing.sm,
    zIndex: 1,
  },
  bulkDropdownStyle: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderColor: "transparent",
    borderWidth: 0,
    borderRadius: radii.sm,
    height: spacing.xl,
    minHeight: spacing.xl,
    paddingLeft: spacing["2xl"],
    paddingHorizontal: spacing.sm,
    paddingVertical: 0,
  },
  bulkDropdownStyleNoIcon: {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    borderColor: "transparent",
    borderWidth: 0,
    borderRadius: radii.sm,
    height: spacing.xl,
    minHeight: spacing.xl,
    paddingHorizontal: spacing.sm,
    paddingVertical: 0,
  },
  bulkDropdownContainerStyle: {
    backgroundColor: colors.white,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
    minWidth: 200,
    color: colors.white,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: spacing.xs,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    paddingHorizontal: spacing.sm,
    height: spacing.xl,
    borderRadius: radii.sm,
  },
  actionButtonPressed: {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
  actionButtonText: {
    fontSize: typographyScale.textXs.fontSize,
    lineHeight: typographyScale.textXs.lineHeight,
    fontWeight: "700",
    color: colors.white,
  },
});
